/**
 * EntryEditorCore — surface-агностичное ядро редактора одной записи (задача 77).
 *
 * §1.1 — расщеплено из `NewEntryFlow.tsx`: ядро владеет state machine
 * (`вид → проект → элемент → часы`; шаг «элемент сборки» — задача 89 §4.2,
 * адресный список элементов проекта резолвится лениво на выбор проекта), draft,
 * pills, валидацией и close-confirm, но НЕ знает про конкретный API — мутации
 * инкапсулирует `EntryEditorAdapter` (self / admin). Прямых импортов записи
 * `api/field-timesheet` (create/replace/delete) тут нет.
 *
 * §2.1 — проп `surface` рулит ТОЛЬКО контейнером и density; шаги, логика,
 * состав записи — общие. Ручной ввод № проекта — единый `NumericPad` на обеих
 * поверхностях (свап №5 на `<input>` откатан, см. `ProjectSearchPanel`):
 *   - `mobile` (дефолт, путь рабочего `/field`) — fullscreen-корень
 *     `fixed inset-0` (как раньше; обёртка `NewEntryFlow` портирует его в
 *     `document.body`).
 *   - `desktop-dock` (внесение за сотрудника, §3.1/§4.1) — правый док 460px
 *     (`<Sheet side=right>`, `patterns.md §Drawer`); ядро владеет своим
 *     `<Sheet>` целиком. Density компактнее (`[data-entry-surface]` в
 *     `tokens.css`).
 *
 * Внутреннее дерево (FlowHeader → PillStack → scroll-body → ActionBar) **одно**
 * на обе поверхности; `ActionBar` уже относительный (`shrink-0 border-t`), в доке
 * естественно встаёт у низа `SheetContent` без `fixed` — `fixed`-StickyBar
 * принадлежит дневной оболочке, не редактору, и тут не используется.
 *
 * Грабли визарда (регрессионный гард — обязаны выжить дословно):
 *   - `projectSkipped` семантика («осознанно пропустил» ≠ «не выбрано»);
 *   - close-confirm только в create-mode при dirty; `goBack` (× на шагах 2+ =
 *     назад без очистки) vs pill-Trash2 (`resetFromStep`);
 *   - ESC → тот же confirm-флоу;
 *   - `searchPickedById` ad-hoc-кэш кода выбранного проекта.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import type { Project, WorkType } from "@/api/field-timesheet";
import { getPairWorkStatus, getProjectAssemblyElements } from "@/api/field-timesheet";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { formatHoursHalf } from "@/lib/timesheet";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";
import {
  FormErrorSummary,
  focusValidationControl,
  type FormValidationIssue,
} from "@/components/ui/form-validation";

import { reducer } from "./reducer";
import { buildPayload, computeLabels, draftFromEntry, isDraftDirty } from "./helpers";
import {
  ActionBar,
  CloseConfirm,
  FlowHeader,
  PillStack,
  Step1WorkType,
  Step2Project,
  Step3Element,
  Step4Hours,
} from "./steps";
import { EMPTY_DRAFT, type EntryEditorCoreProps, type EntrySurface, type Step } from "./types";

export function EntryEditorCore({
  mode,
  initialEntry,
  recentProjects,
  myWorkTypes,
  adapter,
  onClose,
  surface = "mobile",
}: EntryEditorCoreProps): React.ReactElement {
  const { t } = useTranslation();

  // Локальный resolver: id → name. Включает work_type из initialEntry если
  // он внезапно не в `my_work_types` (например, был отключён после создания).
  const workTypesById = React.useMemo(() => {
    const m = new Map<number, WorkType>();
    for (const w of myWorkTypes) {
      if (w.id !== null && w.id !== undefined) {
        m.set(w.id, w);
      }
    }
    return m;
  }, [myWorkTypes]);

  const projectsById = React.useMemo(() => {
    const m = new Map<number, Project>();
    for (const p of recentProjects) {
      if (p.id !== null && p.id !== undefined) m.set(p.id, p);
    }
    return m;
  }, [recentProjects]);

  // Ad-hoc-кэш для проектов, выбранных через ProjectSearchPanel: их нет в
  // recent (рабочий ещё ни разу не писал на этот project_id), поэтому без
  // кэша pill «3. ОБЪЕКТ» падал бы в fallback «№ id» — нарушение правила
  // «не показываем технические ID в UI». Ключ — project_id, значение — code.
  const [searchPickedById, setSearchPickedById] = React.useState<Map<number, string | null>>(
    () => new Map(),
  );

  const [state, dispatch] = React.useReducer(reducer, undefined, () => {
    if (mode === "edit" && initialEntry) {
      return {
        step: 4 as Step,
        draft: draftFromEntry(initialEntry),
      };
    }
    return { step: 1 as Step, draft: EMPTY_DRAFT };
  });

  // §4.2 — адресный список элементов сборки выбранного проекта. Лениво на
  // выбор проекта (day-snapshot проект заранее не знает — отчёт мультипроектный).
  // Ошибка/офлайн (не-recent проект вне кэша) → data пуст → шаг элемента
  // деградирует до пропуска (элемент необязателен), редактор не падает.
  const projectId = state.draft.projectId;
  const elementsQuery = useQuery({
    queryKey: ["field", "assembly-elements", projectId],
    queryFn: () => getProjectAssemblyElements(projectId as number),
    enabled: projectId !== null,
    staleTime: 5 * 60_000,
  });
  const elements = React.useMemo(() => elementsQuery.data ?? [], [elementsQuery.data]);
  const elementsById = React.useMemo(() => {
    const m = new Map<number, string>();
    for (const el of elements) {
      if (el.id !== null && el.id !== undefined) m.set(el.id, el.name);
    }
    return m;
  }, [elements]);

  // §115/2.1 — статус пары (проект, вид работ): закрыта ли работа. Ленивый
  // отдельный запрос (не через adapter — тот про мутации), enabled только на
  // каталожной паре (оба id известны); free-text вид / ручной проект пары не
  // образуют → баннера нет (согласовано с derived `is_rework=False` на сервере).
  // Fail-soft: ошибка/оффлайн → data пуст → баннера нет, запись сохраняется.
  const workTypeId = state.draft.workTypeId;
  const workStatusQuery = useQuery({
    queryKey: ["field-work-status", projectId, workTypeId],
    queryFn: () => getPairWorkStatus(projectId as number, workTypeId as number),
    enabled: projectId !== null && workTypeId !== null,
    staleTime: 5 * 60_000,
  });
  const reworkActive = workStatusQuery.data?.completed === true;
  const reworkCompletedAt = workStatusQuery.data?.completed_at ?? null;

  // Близко к рендеру: лейблы для pills (для уже-зафиксированных шагов).
  const labels = React.useMemo(
    () =>
      computeLabels(state.draft, workTypesById, projectsById, searchPickedById, elementsById, {
        projectSkipped: t("field.entryFlow.pill.projectSkipped"),
        elementSkipped: t("field.entryFlow.pill.elementSkipped"),
        elementFallback: t("field.entryFlow.pill.elementFallback"),
      }),
    [state.draft, workTypesById, projectsById, searchPickedById, elementsById, t],
  );

  const saving = adapter.isSaving;
  const deleting = adapter.isDeleting;
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const hoursIssue: FormValidationIssue | undefined =
    submitAttempted && state.draft.hoursHalf === null
      ? {
          id: "field-entry-flow-hours-error",
          message: t("field.entryFlow.step4.required"),
          kind: "field",
          controlId: "field-entry-flow-step4-stepper-inc",
        }
      : undefined;
  const saveIssue: FormValidationIssue | undefined = adapter.saveError ?? undefined;
  const submitIssues = [hoursIssue, saveIssue].filter(
    (issue): issue is FormValidationIssue => issue != null,
  );

  // Handlers ------------------------------------------------------------------

  function handleSubmit(): void {
    if (state.draft.hoursHalf === null) {
      setSubmitAttempted(true);
      requestAnimationFrame(() => focusValidationControl("field-entry-flow-step4-stepper-inc"));
      return;
    }
    const payload = buildPayload(state.draft);
    if (!payload) return;
    if (mode === "edit" && initialEntry && initialEntry.id != null) {
      adapter.replace(initialEntry.id, payload);
    } else {
      adapter.create(payload);
    }
  }

  // Close-confirm: только в create-mode когда что-то заполнено.
  const draftDirty = React.useMemo(() => isDraftDirty(state.draft), [state.draft]);
  useReleaseDirtyState(draftDirty);
  const [closeConfirmOpen, setCloseConfirmOpen] = React.useState(false);

  function handleCloseRequest(): void {
    // §20/8.3: × вверху в create-mode на шагах 2+ — назад на шаг, не reset.
    // В edit-mode шаг всегда 4, поведение «× = закрыть» сохраняем (правки
    // существующей записи закрываются без подтверждения).
    if (mode === "create" && state.step > 1) {
      dispatch({ type: "goBack" });
      return;
    }
    if (mode === "create" && draftDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    onClose();
  }

  // ESC закрывает с тем же confirm-флоу.
  React.useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCloseRequest();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftDirty, mode, state.step]);

  // Render --------------------------------------------------------------------

  // Внутреннее дерево — одно на обе поверхности. Контейнер выбирается ниже.
  const inner = (
    <>
      <FlowHeader
        mode={mode}
        title={t(`field.entryFlow.step${state.step}.title`)}
        stepLabel={t("field.entryFlow.stepCounter", {
          current: state.step,
          total: 4,
        })}
        onClose={handleCloseRequest}
      />

      <PillStack
        step={state.step}
        labels={labels}
        reworkActive={reworkActive}
        onReset={(s) => dispatch({ type: "resetFromStep", step: s })}
      />

      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="field-entry-flow-body">
        {state.step === 1 && (
          <Step1WorkType
            myWorkTypes={myWorkTypes}
            onPickStandard={(wt) =>
              dispatch({
                type: "selectWorkType",
                id: wt.id as number,
              })
            }
            onPickCustom={(text) => dispatch({ type: "selectWorkTypeText", text })}
          />
        )}
        {state.step === 2 && (
          <Step2Project
            recentProjects={recentProjects}
            onPickRecent={(id) => dispatch({ type: "selectProjectId", projectId: id })}
            onPickHit={(v) => {
              if (v.kind === "id") {
                // Запоминаем code для pill «2. ОБЪЕКТ» — recentProjects может
                // не содержать только что выбранный проект до refetch.
                setSearchPickedById((prev) => {
                  const next = new Map(prev);
                  next.set(v.projectId, v.code);
                  return next;
                });
                dispatch({ type: "selectProjectId", projectId: v.projectId });
              } else {
                dispatch({ type: "selectProjectRaw", code: v.code });
              }
            }}
            onSkip={() => dispatch({ type: "skipProject" })}
          />
        )}
        {state.step === 3 && (
          <Step3Element
            elements={elements}
            loading={elementsQuery.isLoading}
            onPick={(id) => dispatch({ type: "selectElement", id })}
            onSkip={() => dispatch({ type: "skipElement" })}
          />
        )}
        {state.step === 4 && (
          <Step4Hours
            value={state.draft.hoursHalf}
            onChange={(half) => dispatch({ type: "setHours", half })}
            reworkActive={reworkActive}
            reworkCompletedAt={reworkCompletedAt}
            reworkNote={state.draft.reworkNote}
            onReworkNoteChange={(note) => dispatch({ type: "setReworkNote", note })}
            error={hoursIssue}
          />
        )}
      </div>

      {/* Action bar. На шагах 1–3 не показываем (выбор сам двигает state
          machine). На шаге 4 — submit + delete (edit-mode). `shrink-0` держит
          его у низа колонки и в fullscreen, и в доке — без `fixed`. */}
      {state.step === 4 && (
        <div>
          <div className="px-4 pb-3">
            <FormErrorSummary
              issues={submitIssues}
              countLabel={(count) => t("validation.fieldErrorCount", { count })}
              data-testid="field-entry-flow-error-summary"
            />
          </div>
          <ActionBar
            submitLabel={t("field.entryFlow.submitCta", {
              hours: formatHoursHalf(state.draft.hoursHalf ?? 0),
            })}
            submitDisabled={saving || deleting}
            submitting={saving}
            onSubmit={handleSubmit}
            showDelete={mode === "edit" && (initialEntry?.id ?? null) !== null}
            deleting={deleting}
            deleteTarget={[labels.step1, labels.step2, formatHoursHalf(state.draft.hoursHalf ?? 0)]
              .filter((part): part is string => Boolean(part))
              .join(" · ")}
            onDeleteRequest={async () => {
              if (initialEntry?.id != null) await adapter.remove(initialEntry.id);
            }}
          />
        </div>
      )}

      <CloseConfirm
        open={closeConfirmOpen}
        onCancel={() => setCloseConfirmOpen(false)}
        onConfirm={() => {
          setCloseConfirmOpen(false);
          onClose();
        }}
      />
    </>
  );

  if (surface === "desktop-dock") {
    return (
      <Sheet open onOpenChange={(v) => !v && handleCloseRequest()}>
        <SheetContent
          side="right"
          showCloseButton={false}
          style={{ width: 460, maxWidth: "100vw" }}
          className="!w-[460px] !max-w-[100vw] gap-0 p-0"
          data-entry-surface={surface}
          data-testid="field-entry-flow"
          data-mode={mode}
          data-step={state.step}
        >
          {/* sr-only title/description — визуальный заголовок несёт FlowHeader,
              но Radix Dialog требует доступного имени (иначе aria-варнинг). */}
          <SheetTitle className="sr-only">{t("field.entryFlow.dockTitle")}</SheetTitle>
          <SheetDescription className="sr-only">
            {t("field.entryFlow.dockDescription")}
          </SheetDescription>
          {inner}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col"
      style={{ background: "var(--bg-panel-0)" }}
      data-entry-surface={surface}
      data-testid="field-entry-flow"
      data-mode={mode}
      data-step={state.step}
      role="dialog"
      aria-modal="true"
    >
      {inner}
    </div>
  );
}

// Re-export для оболочек §3.1/§4.1, которым нужен тип поверхности.
export type { EntrySurface };
